import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { CreatorResponseDto, CreatorService } from '@/app/services/creator/creator.service';
import { ImageService } from '@/app/services/image/image.service';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { formatDistanceToNow } from 'date-fns';

interface CreatorsFilterResponse {
  count: number;
  data: CreatorResponseDto[];
}

@Component({
  selector: 'ins-creator-list',
  imports: [RouterOutlet, EmptyStateComponent],
  templateUrl: './creator-list.html',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreatorList implements OnInit {
  private static readonly PAGE_SIZE = 25;
  private static readonly SCROLL_LOAD_THRESHOLD_PX = 80;

  creators = signal<CreatorResponseDto[] | undefined>(undefined);
  creatorsCount = signal(0);
  creatorsLoading = signal(false);
  creatorsLoadingMore = signal(false);
  searchQuery = signal('');
  selectedCreatorId = signal<string | undefined>(undefined);

  private pageNumber = signal(0);
  private searchSessionId = signal(0);
  private searchTrigger = new Subject<string>();
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private creatorService = inject(CreatorService);
  private toastNotificationService = inject(ToastNotificationService);
  readonly imageService = inject(ImageService);

  ngOnInit(): void {
    this.syncSelectedFromRoute();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncSelectedFromRoute());

    merge(of(''), this.searchTrigger.pipe(debounceTime(300), distinctUntilChanged()))
      .pipe(
        tap(() => {
          this.pageNumber.set(0);
          this.creatorsLoadingMore.set(false);
          this.searchSessionId.update((n) => n + 1);
        }),
        switchMap((term) => {
          this.creatorsLoading.set(true);
          return this.fetchCreators(0, term).pipe(
            tap({
              next: () => this.creatorsLoading.set(false),
              error: () => this.creatorsLoading.set(false),
            }),
            catchError((err) => {
              console.error('Error fetching creators:', err);
              this.toastNotificationService.alert({
                title: 'Error fetching creators',
                message: 'Failed to retrieve creators.',
                type: 'error',
                icon: 'error',
              });
              return of({ count: 0, data: [] as CreatorResponseDto[] });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.creators.set(response.data);
        this.creatorsCount.set(response.count);
      });
  }

  timeSinceCreated(createdDate: Date | string | undefined): string {
    if (createdDate == null) {
      return '—';
    }
    const d = typeof createdDate === 'string' ? new Date(createdDate) : createdDate;
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    return formatDistanceToNow(d, { addSuffix: true });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchTrigger.next(value.trim());
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollHeight - scrollTop - clientHeight > CreatorList.SCROLL_LOAD_THRESHOLD_PX) {
      return;
    }
    this.loadMoreIfNeeded();
  }

  isSelectedCreator(creator: CreatorResponseDto): boolean {
    return this.selectedCreatorId() === creator.blazeFirebaseUid;
  }

  onCreatorSelect(creator: CreatorResponseDto): void {
    this.selectedCreatorId.set(creator.blazeFirebaseUid);
    void this.router.navigate(['/insights/creator', creator.blazeFirebaseUid, 'profile']);
  }

  private loadMoreIfNeeded(): void {
    if (this.creatorsLoading() || this.creatorsLoadingMore()) {
      return;
    }
    const list = this.creators();
    if (list === undefined || list.length >= this.creatorsCount()) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    const term = this.searchQuery().trim();
    const session = this.searchSessionId();
    this.creatorsLoadingMore.set(true);

    this.fetchCreators(nextPage, term)
      .pipe(
        catchError(() => of({ count: 0, data: [] as CreatorResponseDto[] })),
        finalize(() => this.creatorsLoadingMore.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (session !== this.searchSessionId()) {
          return;
        }
        if (response.data.length === 0) {
          this.creatorsCount.set((this.creators() ?? []).length);
          return;
        }
        this.creators.update((prev) => [...(prev ?? []), ...response.data]);
        this.pageNumber.set(nextPage);
      });
  }

  private fetchCreators(page: number, searchTerm: string): Observable<CreatorsFilterResponse> {
    const term = searchTerm.trim();
    return this.creatorService.inviteContentCreatorsFilterV2({
      searchTerm: term || undefined,
      skip: page * CreatorList.PAGE_SIZE,
      take: CreatorList.PAGE_SIZE,
      sortField: 'creator.createdDate',
      sortOrder: 'DESC',
    }) as Observable<CreatorsFilterResponse>;
  }

  private syncSelectedFromRoute(): void {
    const creatorId = this.route.firstChild?.snapshot.paramMap.get('creatorId');
    this.selectedCreatorId.set(creatorId ?? undefined);
  }
}
